'use client'

import { useRef, useState } from 'react'
import content from '@/content/demo.json'
import { track } from '@/lib/analytics'
import { getPostHogTracingHeaders, identifyFromEmail, setUserProperties } from '@/lib/posthog'
import { isPersonalEmailProvider } from '@/lib/work-email'

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
const BOOKING_URL = 'https://calendar.app.google/RY8kt9tBiiAaCsRa8'
const RETRY_MESSAGE = 'We could not submit your request. Please try again.'

export default function BookDemoPage() {
  const [form, setForm] = useState({ name: '', email: '', company: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'submitting'>('idle')
  const [hasStarted, setHasStarted] = useState(false)
  const fieldRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const update = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setForm((current) => ({ ...current, [name]: value }))
    setErrors((current) => ({ ...current, [name]: '' }))
    if (!hasStarted && value.trim()) {
      setHasStarted(true)
      track('demo_form_started', { form_variant: 'monaco' })
    }
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const next: Record<string, string> = {}
    if (!form.name.trim()) next.name = 'Enter your name.'
    if (!form.email.trim()) next.email = 'Enter your work email.'
    else if (!EMAIL_REGEX.test(form.email)) next.email = 'Enter a valid work email.'
    else if (isPersonalEmailProvider(form.email)) next.email = 'Please enter your work email.'
    if (!form.company.trim()) next.company = 'Enter your company.'
    if (Object.keys(next).length) {
      setErrors(next)
      const firstInvalid = Object.keys(next)[0]
      requestAnimationFrame(() => fieldRefs.current[firstInvalid]?.focus())
      return
    }

    setStatus('submitting')
    track('demo_form_submit_attempted', { form_variant: 'monaco' })
    let failureMessage = RETRY_MESSAGE
    try {
      const response = await fetch('/api/submit-demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getPostHogTracingHeaders() },
        body: JSON.stringify({
          firstName: form.name,
          lastName: '',
          email: form.email,
          company: form.company,
          phone: '',
          comments: '',
          source: 'book_demo_calendar',
        }),
      })
      const result: unknown = await response.json().catch(() => null)
      if (!response.ok) {
        if (
          result
          && typeof result === 'object'
          && 'error' in result
          && typeof result.error === 'string'
          && result.error.trim()
        ) {
          failureMessage = result.error
        }
        throw new Error('Demo submission failed')
      }
      identifyFromEmail(form.email, { name: form.name, company: form.company, lead_source: 'book_demo_form' })
      setUserProperties({ company: form.company, last_demo_request_at: new Date().toISOString() })
      track('demo_calendar_redirected', { form_variant: 'monaco' })
      window.location.assign(BOOKING_URL)
    } catch {
      setStatus('idle')
      setErrors({ form: failureMessage })
    }
  }

  return <section className="fp-demo-page">
    <div className="fp-demo-grid" aria-hidden="true" />
    <div className="fp-demo-panel">
      <div className="fp-demo-copy">
        <h1>{content.title}</h1>
        <ul className="fp-demo-outcomes" aria-label="Expected outcomes">
          {content.outcomes.map((outcome) => <li key={outcome}><span aria-hidden="true">✓</span>{outcome}</li>)}
        </ul>
      </div>
      <form className="ph-no-capture" onSubmit={submit} noValidate>
        {Object.keys(errors).some((key) => key !== 'form') && <p className="fp-demo-error-summary" role="alert">Check the highlighted fields and try again.</p>}
        {(['name', 'email', 'company'] as const).map((name) => <label key={name} htmlFor={`demo-${name}`}><span>{content.fields[name]}</span><input ref={(element) => { fieldRefs.current[name] = element }} id={`demo-${name}`} name={name} type={name === 'email' ? 'email' : 'text'} value={form[name]} onChange={update} autoComplete={name === 'email' ? 'email' : name === 'company' ? 'organization' : 'name'} required aria-required="true" aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `demo-${name}-error` : undefined} />{errors[name] && <small id={`demo-${name}-error`} role="alert">{errors[name]}</small>}</label>)}
        {errors.form && <p className="fp-demo-error" role="alert">{errors.form}</p>}
        <button className="fp-button" type="submit" disabled={status === 'submitting'}>{status === 'submitting' ? content.submitting : content.submit}<span aria-hidden="true">→</span></button>
      </form>
    </div>
  </section>
}
